import { Box, Container, List } from "@mui/material";
import { React, useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PageActions from "../components/Common/Buttons/PageActions";
import ErrorMessage from "../components/Common/ErrorMessage";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import PageTitle from "../components/Common/PageTitle";
import ShoppingListItem from "../components/ShoppingList/ShoppingListItem";
import { MenuPlanContext } from "../contexts/MenuPlanContext";
import { RecipeContext } from "../contexts/RecipeContext";
import { ShoppingListContext } from "../contexts/ShoppingListContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useShoppingListTableData from "../hooks/useShoppingListTableData";
import { itemList, itemWrapper, pageContainer, pageHeaderContainer } from "../styles/shoppingListStyles";

const ShoppingListPage = () => {
  const { t } = useTranslation();
  const { items, setItems, setGeneratedItems, saveItems, frequentItems, loading, error } = useContext(ShoppingListContext);
  const { recipes } = useContext(RecipeContext);
  const { menuPlans } = useContext(MenuPlanContext);

  useDocumentTitle("shoppingList");

  const generateShoppingList = useCallback(() => {
    const shoppingList = [];

    menuPlans.forEach(({ recipeId, day, meal }) => {
      const recipe = recipes.find((r) => Number(r.id) === Number(recipeId));
      if (recipe) {
        recipe.ingredients.forEach(({ name, quantity, unit, notes }) => {
          const lowerCaseName = name.toLowerCase();
          const existingItem = shoppingList.find((item) => item.name.toLowerCase() === lowerCaseName && item.unit === unit);

          if (existingItem) {
            existingItem.quantity = Number(existingItem.quantity) + Number(quantity);
            existingItem.recipes.push({ title: recipe.title, day, meal });
          } else {
            shoppingList.push({
              name,
              quantity,
              unit,
              notes,
              recipes: [{ title: recipe.title, day, meal }],
            });
          }
        });
      }
    });

    shoppingList.sort((a, b) => a.name.localeCompare(b.name));
    return shoppingList;
  }, [menuPlans, recipes]);

  // Generate items on mount but do NOT overwrite user's saved list
  useEffect(() => {
    if (!loading) {
      const generated = generateShoppingList(menuPlans, recipes);
      setGeneratedItems(generated);

      const mergedItems = [...generated];

      // Only set items if no user modifications exist
      if (items.length === 0) {
        setItems(mergedItems);
      }
    }
  }, [loading, menuPlans, recipes, setGeneratedItems, items.length, setItems, frequentItems, generateShoppingList]);

  // Handler to reset to the freshly generated list
  const handleReset = () => {
    const freshList = generateShoppingList(menuPlans, recipes);
    setItems(freshList);
    saveItems(freshList);
  };

  const handleUpdateItem = useCallback(
    (index, updatedItem) => {
      const updatedItems = [...items];
      updatedItems[index] = updatedItem;
      setItems(updatedItems);
      saveItems(updatedItems);
    },
    [items, setItems, saveItems],
  );

  const handleDeleteItem = useCallback(
    (index) => {
      const updatedItems = [...items];
      updatedItems.splice(index, 1);
      setItems(updatedItems);
      saveItems(updatedItems);
    },
    [items, setItems, saveItems],
  );

  const { tableHeaders, tableRows } = useShoppingListTableData(items);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage error={error.message} />;

  return (
    <Container sx={pageContainer} disableGutters>
      <Box sx={pageHeaderContainer}>
        <PageTitle text={t("shoppingListTitle")} />

        <PageActions onReset={handleReset} tableHeaders={tableHeaders} tableRows={tableRows} fileName={t("shoppingListTitle")} />
      </Box>

      <List sx={itemList}>
        {items.map((item, index) => (
          <div key={index} style={itemWrapper}>
            {/* {item.conflict ? (
              <ShoppingListItemWithConflict
                ingredientGroup={item}
                onUpdate={(resolvedItem) => {
                  // Update the shopping list state with the resolved item
                  const updatedItems = [...items];
                  updatedItems[index] = resolvedItem;
                  setItems(updatedItems);
                  saveItems(updatedItems); // Save changes to the database
                }}
              />
            ) : (
              <ShoppingListItem key={index} item={item} index={index} />
            )} */}
            <ShoppingListItem key={index} item={item} index={index} onUpdateItem={handleUpdateItem} onDeleteItem={handleDeleteItem} />
          </div>
        ))}
      </List>
    </Container>
  );
};

export default ShoppingListPage;
